"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONTRACT_DOMAIN_ACTIONS = exports.TOKEN_SCOPE_FOR_ACTION = exports.tokenScopeSchema = exports.TOKEN_SCOPES = void 0;
exports.narrowByScopes = narrowByScopes;
exports.actionsForScopes = actionsForScopes;
exports.scopesForAction = scopesForAction;
const zod_1 = require("zod");
const catalogue_1 = require("./catalogue");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C51.1 · what a personal access token may do, as a FILTER. `decisions.md`  │
 * │ R-39.                                                                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ```
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A SCOPE IS A FILTER, NEVER A GRANT. THE EFFECTIVE AUTHORITY OF A  │
 * │ REQUEST IS `intersect(member permissions, token scopes)`, AND THE      │
 * │ INTERSECTION IS THE ONLY DIRECTION THAT EXISTS.                       │
 * └───────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * `mergeRoles` is still the whole of what a member may do — most permissive wins, checked by the
 * service on every call (rule 6). This narrows the result for a request that arrived carrying a
 * token, so the worst a leaked token can do is what its holder could already do, and usually less.
 * A scope that could **add** an action would be a second permission model, reachable by anybody who
 * can mint a token.
 *
 * ⚠️ **This is applied in `CheckService`, once, where the permissions are loaded** — not at the
 * edge and not per endpoint. R-39 makes every endpoint in the product reachable with a token, so a
 * filter that lived in the MCP layer would leave `forecastify:read` able to `POST /worklogs`. One
 * check service, per rule 6, is also the only place this can be correct.
 *
 * ⚠️ **It lives in `@forecastify/shared` beside `mergeRoles` for that function's own reason**: the
 * browser and the server must not hold two copies of a predicate about authority, because *"the
 * copy that drifts is the one deciding what a UI offers while the server quietly disagrees"*.
 * Nothing in the browser holds a token today; the day something does, it reads this.
 */
exports.TOKEN_SCOPES = [
    'forecastify:read',
    'forecastify:write',
    'forecastify:contracts',
];
exports.tokenScopeSchema = zod_1.z.enum(exports.TOKEN_SCOPES);
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ EVERY ACTION IS CLASSIFIED EXPLICITLY, AND `Record<ActionKey, …>` IS │
 * │ WHAT MAKES THAT CHECKABLE — A NEW ACTION FAILS THE TYPECHECK.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The tempting version is a predicate — *"an action ending in `.view` or `.read` is a read"* — and
 * it is wrong on the first action that does not follow the pattern: **`report.export` reads and
 * writes nothing**, and a heuristic files it under `forecastify:write`, so a read-only token cannot
 * export a report it can perfectly well see. `registry.test.ts` uses that same predicate for a
 * different question and is right to, because a tool's `action` is checked against it as a *gate*;
 * here the answer decides what a credential may do, so it is data rather than a guess.
 *
 * ⚠️ **The exhaustiveness is the TYPE's, not a test's**, which is the stronger of the two: the
 * annotation is `Record<ActionKey, TokenScope>`, so an action added to the catalogue without a
 * classification here does not reach a test — it fails the build, in the package every other
 * package depends on. Deny by default, expressed as a compiler error.
 */
exports.TOKEN_SCOPE_FOR_ACTION = {
    /* ── reads ──────────────────────────────────────────────────────────────────────────────── */
    'task.view': 'forecastify:read',
    'worklog.read': 'forecastify:read',
    'project.view': 'forecastify:read',
    'people.view': 'forecastify:read',
    'leave.view': 'forecastify:read',
    'settings.view': 'forecastify:read',
    'report.view': 'forecastify:read',
    /* ⚠️ A read, and the reason the map above is data. An export produces a file out of rows the
     * caller may already see; it mutates nothing, so refusing it to a read-only token would be a
     * restriction nobody asked for and nobody could explain. */
    'report.export': 'forecastify:read',
    'contract.view': 'forecastify:read',
    /* R-58 · the finance dashboard. */
    'project.finance': 'forecastify:read',
    /* ⚠️ A read, and the whole of what it gates is whether the four money MEASURES are offered —
     * C60. It writes nothing. The step-up is handled below rather than here. */
    'report.money': 'forecastify:read',
    /* ⚠️ R-46 · the client list, split out of `client.manage` so it can be read without being edited. */
    'client.view': 'forecastify:read',
    /* U-8 · the workspace audit log. A read: it changes nothing, however much it shows. */
    'audit.view': 'forecastify:read',
    'workspace.create': 'forecastify:write',
    /* 22 Oct 2026 · the workspace menu and the dashboards: what can be SEEN. */
    'menu.projects': 'forecastify:read',
    'menu.tasks': 'forecastify:read',
    'menu.contracts': 'forecastify:read',
    'menu.invoices': 'forecastify:read',
    'menu.worklogs': 'forecastify:read',
    'menu.people': 'forecastify:read',
    'menu.clients': 'forecastify:read',
    'dashboard.resource': 'forecastify:read',
    'dashboard.logging': 'forecastify:read',
    'dashboard.utilization': 'forecastify:read',
    'dashboard.leave': 'forecastify:read',
    /* 20 Oct 2026 · Phase 6 · DM-1 — reading deals, and changing them. */
    'deal.view': 'forecastify:read',
    'deal.manage': 'forecastify:write',
    /* 16 Oct 2026 · Kockázatkezelés. A read: three tables, nothing written. */
    /* ── writes ─────────────────────────────────────────────────────────────────────────────── */
    'task.create': 'forecastify:write',
    'task.update': 'forecastify:write',
    'task.assign': 'forecastify:write',
    'task.schedule': 'forecastify:write',
    'task.delete': 'forecastify:write',
    'worklog.create': 'forecastify:write',
    'worklog.update': 'forecastify:write',
    'worklog.lock': 'forecastify:write',
    'project.create': 'forecastify:write',
    'project.manage': 'forecastify:write',
    'client.manage': 'forecastify:write',
    'people.manage': 'forecastify:write',
    'people.onboard': 'forecastify:write',
    'people.deactivate': 'forecastify:write',
    'leave.decide': 'forecastify:write',
    /* ⚠️ R-48 · filing a leave request writes a row, so it is a write scope like deciding one. */
    'leave.request': 'forecastify:write',
    'report.manage': 'forecastify:write',
    'settings.manage': 'forecastify:write',
    'integration.manage': 'forecastify:write',
    'billing.manage': 'forecastify:write',
    'contract.manage': 'forecastify:write',
    'lineitem.manage': 'forecastify:write',
};
/**
 * Actions that need `forecastify:contracts` **in addition to** their read or write scope.
 *
 * ⚠️ **An additional requirement, not an alternative one**, which is what makes it the step-up
 * shape the MCP specification's scope-minimization guidance asks for: a token starts with
 * `forecastify:read`, and the contract domain is challenged separately when it is first wanted.
 *
 * ⚠️ **It is not the only gate on these three.** `ContractService.gate` asks the `contracts.view`
 * feature flag and then the permission, on every call, and both still run. This narrows what a
 * credential carries; it decides nothing about what a workspace has switched on.
 */
exports.CONTRACT_DOMAIN_ACTIONS = [
    'contract.view',
    'contract.manage',
    'lineitem.manage',
];
/**
 * ⚠️⚠️ **AND `report.money` IS DELIBERATELY NOT IN THAT LIST, WHICH IS WORTH AN ARGUMENT.**
 * C60's four money measures are cost, revenue, margin and margin % — the same figures
 * `contract.view` is stepped up for, grouped by person or project instead of by contract. So a
 * `forecastify:read` token can pull every margin in the workspace through the report builder while
 * the contract endpoints refuse it, which is a gate holding the front door with the side one open.
 *
 * ⚠️ **It was added to the list and taken back out**, because `token-scopes.test.ts` asserts that
 * every contract-domain action starts with `contract.` or `lineitem.` — a tested invariant saying
 * the step-up is about the contract RESOURCE rather than about financial data in general. That is a
 * deliberate statement by whoever wrote R-39, and a merge is the wrong place to overrule it.
 *
 * ⚠️ **Flagged rather than decided, per CLAUDE.md rule 8.** `report.money` was minted by C60 and
 * this list predates it; nobody has ruled on whether the contract step-up should cover the reports
 * that read the same numbers. Whoever owns the token model should say, and the answer is either a
 * fourth scope or a wider invariant — not a quiet exception here.
 */
/**
 * The caller's permissions as narrowed by the credential they presented.
 *
 * ⚠️ **`undefined` means no token was presented and returns the input unchanged.** A session, a
 * background job and a test fixture all authenticate without a token, and for them the member's
 * own permissions are the whole answer — there is nothing to intersect with.
 *
 * ```
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ `fieldRules` PASSES THROUGH UNTOUCHED, AND FILTERING IT THE SAME  │
 * │ WAY WOULD BE A PRIVILEGE ESCALATION RATHER THAN A NARROWING.          │
 * └───────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * A grant is permission to do something, so dropping one removes authority. **A field rule is a
 * RESTRICTION on a resource the member already reaches** — `hidden` on `task.internal_note` is the
 * only thing keeping an Alvállalkozó out of it (migration `0009`) — so dropping one *grants* sight
 * of a field the role was configured to hide. The two halves of `EffectivePermissions` are opposite
 * in sign, and a loop that treated them alike would read as symmetry and behave as a leak.
 */
function narrowByScopes(permissions, scopes) {
    if (scopes === undefined)
        return permissions;
    const held = new Set(scopes);
    const contractDomain = new Set(exports.CONTRACT_DOMAIN_ACTIONS);
    const grants = {};
    for (const [action, scope] of Object.entries(permissions.grants)) {
        /* ⚠️ An action the map does not know is dropped rather than kept. It can only be a grant row
         * for an action this build no longer declares — a role edited before a rename, say — and
         * "unclassified" must not mean "unrestricted" on a credential path. */
        const required = exports.TOKEN_SCOPE_FOR_ACTION[action];
        if (required === undefined || !held.has(required))
            continue;
        if (contractDomain.has(action) && !held.has('forecastify:contracts'))
            continue;
        grants[action] = scope;
    }
    return { grants, fieldRules: permissions.fieldRules };
}
/**
 * Every action a scope set reaches, for a `WWW-Authenticate` challenge and for the tool filter.
 *
 * ⚠️ Derived from the same map rather than from a second list, so a challenge cannot name a scope
 * that grants something else.
 */
function actionsForScopes(scopes) {
    const held = new Set(scopes);
    const contractDomain = new Set(exports.CONTRACT_DOMAIN_ACTIONS);
    return catalogue_1.ACTIONS.filter((action) => {
        const required = exports.TOKEN_SCOPE_FOR_ACTION[action];
        if (!held.has(required))
            return false;
        return !contractDomain.has(action) || held.has('forecastify:contracts');
    });
}
/**
 * The scopes a given action needs — what a `403 insufficient_scope` challenge must name.
 *
 * ⚠️ **All of them at once.** The specification is explicit that challenging incrementally forces a
 * round trip per scope and degrades the experience for one operation; a contract write needs
 * `forecastify:write` **and** `forecastify:contracts`, and the challenge says both.
 */
function scopesForAction(action) {
    const required = exports.TOKEN_SCOPE_FOR_ACTION[action];
    return exports.CONTRACT_DOMAIN_ACTIONS.includes(action)
        ? [required, 'forecastify:contracts']
        : [required];
}
//# sourceMappingURL=token-scopes.js.map